/**
 * The five sections of `/konto`, their order, and how the chosen one rides in the address.
 *
 * **Profil · Kolor marki · Subskrypcja · Aplikacja · Integracje**, at the level of the page. The set is
 * the owner's, settled against `profile-web-mockups-v2/f-zakladki.html`: no notifications tab, no
 * coaching-mode tab, no sign-in tab, and bookings get a section of their own elsewhere later.
 * Kalendarz (the Google calendar connection) was added by the owner on 17 October 2026, with no
 * handoff, and is offered only where the environment has Google configured (`visibleAccountTabs`).
 * It stands LAST (19 October 2026) so that appearing once its read lands moves no other tab. On
 * 26 October 2026 the owner renamed it Integracje (more integrations are coming); the calendar is
 * its first section, and the old `?tab=kalendarz` still opens it (`LEGACY_TABS`).
 *
 * ── The keys are Polish because the app's addresses are ──────────────────────────────────────
 * `/klienci`, `/plans/nowy`, `/raporty?tab=kto`, and now `/konto?tab=kolor-marki`. A URL is
 * something a person reads, and this app reads Polish to its trainers. There is no mobile screen
 * here, so there is no deep link to keep compatible.
 *
 * An unrecognised `?tab=` is DROPPED rather than defaulted into the URL, and so is the default —
 * `app/routes/_shell.konto.tsx` carries that rule and its reason.
 */

import { isGoogleConnectOutcome, type GoogleConnectOutcome } from './googleCalendar';
import { normalizeTrialCode } from './trialCode';

export const ACCOUNT_TABS = ['profil', 'kolor-marki', 'subskrypcja', 'aplikacja', 'integracje'] as const;

export type AccountTab = (typeof ACCOUNT_TABS)[number];

/** Addresses from before a rename, and the tab they now name. */
const LEGACY_TABS: Readonly<Record<string, AccountTab>> = { kalendarz: 'integracje' };

/** The strip's tabs, in order; Integracje only when `calendarTabShown` says so. */
export function visibleAccountTabs(calendarShown: boolean): readonly AccountTab[] {
  return calendarShown ? ACCOUNT_TABS : ACCOUNT_TABS.filter((tab) => tab !== 'integracje');
}

export const DEFAULT_ACCOUNT_TAB: AccountTab = 'profil';

export function isAccountTab(value: unknown): value is AccountTab {
  return typeof value === 'string' && (ACCOUNT_TABS as readonly string[]).includes(value);
}

/** What `?checkout=` may say: the payment page sent the trainer back, or they backed out of it. */
export const CHECKOUT_RETURNS = ['return', 'cancel'] as const;

export type CheckoutReturn = (typeof CHECKOUT_RETURNS)[number];

export function isCheckoutReturn(value: unknown): value is CheckoutReturn {
  return typeof value === 'string' && (CHECKOUT_RETURNS as readonly string[]).includes(value);
}

/** The provider's own session id is bounded at the wire's own ceiling for the field. */
const SESSION_ID_MAX = 256;

/**
 * The screen's search parameters — an optional member per parameter, absent meaning "not in the
 * URL", shaped as `/raporty`'s is.
 *
 * ── The two checkout parameters are the payment page's, not ours ────────────────────────────
 * The backend builds the return address and puts `?checkout=return&session_id=…` on it, so this
 * validator's job is to recognise them rather than to define them. `session_id` keeps the
 * provider's spelling for that reason: it is a name on somebody else's URL, and renaming it here
 * would mean the address the trainer actually comes back to is not the one this screen reads.
 *
 * They are DROPPED from the address as soon as they have been read (`lib/useWebCheckout`): a
 * return URL is not a link to share, and reloading one would re-run the completion call.
 */
export interface AccountSearch {
  readonly tab?: AccountTab;
  readonly checkout?: CheckoutReturn;
  readonly session_id?: string;
  /**
   * A trial code a link carries (subscription R19), normalised. It fills the Pro card's code row,
   * and like the two checkout parameters it is dropped from the address once read. The backend's
   * cancel URL puts it back beside `?checkout=cancel`, so backing out of a trial's payment page lands
   * on the card with the code still applied.
   */
  readonly code?: string;
  /** What the Google return page reports (`/konto/google`); dropped once read. */
  readonly google?: GoogleConnectOutcome;
}

/**
 * The route's `validateSearch`. It lives in the slice rather than in `app/routes/`, because which
 * parameters this screen understands is the screen's decision.
 */
export function validateAccountSearch(raw: Record<string, unknown>): AccountSearch {
  const asked = typeof raw.tab === 'string' ? (LEGACY_TABS[raw.tab] ?? raw.tab) : raw.tab;
  const tab = isAccountTab(asked) ? asked : undefined;
  const checkout = isCheckoutReturn(raw.checkout) ? raw.checkout : undefined;
  const google = isGoogleConnectOutcome(raw.google) ? raw.google : undefined;
  const sessionId =
    typeof raw.session_id === 'string' && raw.session_id.length <= SESSION_ID_MAX
      ? raw.session_id
      : undefined;
  const code = normalizeTrialCode(raw.code);

  // OMITTED rather than set to `undefined`: TanStack Router serialises the object back into the URL
  // and an explicit `undefined` leaves a bare `?tab=` behind.
  return {
    ...(tab === undefined || tab === DEFAULT_ACCOUNT_TAB ? {} : { tab }),
    ...(checkout === undefined ? {} : { checkout }),
    // Only ever beside a `?checkout=`: an id on its own names no act and would ride in the address
    // for the rest of the session.
    ...(checkout === undefined || sessionId === undefined ? {} : { session_id: sessionId }),
    ...(code === null ? {} : { code }),
    ...(google === undefined ? {} : { google }),
  };
}

/**
 * Which tab an address is asking for. Anything this screen does not recognise is Profil — except
 * that a trial code always means Subskrypcja: a link carrying one is about the Pro card, whatever
 * else it says, and the code is read by that tab alone.
 */
export function accountTabOf(search: AccountSearch): AccountTab {
  if (normalizeTrialCode(search.code) !== null) return 'subskrypcja';
  return isAccountTab(search.tab) ? search.tab : DEFAULT_ACCOUNT_TAB;
}

/**
 * Whether an address names a tab this screen does not draw: a word that is no tab at all, or
 * Integracje (or its old address, Kalendarz) where the strip does not offer it (R12). Either way the
 * screen draws Profil, and the address is put back to it rather than keep `?tab=integracje` over the
 * Profil panel. `raw` is the router's own value: it keeps a key the validator dropped.
 */
export function addressNamesUndrawnTab(raw: unknown, calendarShown: boolean): boolean {
  if (typeof raw !== 'string') return false;
  const asked = LEGACY_TABS[raw] ?? raw;
  return !isAccountTab(asked) || (asked === 'integracje' && !calendarShown);
}

/** The same address without its tab — every other parameter is left for the hook that reads it. */
export function withoutTab(search: AccountSearch): AccountSearch {
  return Object.fromEntries(Object.entries(search).filter(([key]) => key !== 'tab'));
}

/** The address a tab should be at. The default serialises to nothing — see the header. */
export function accountTabSearch(tab: AccountTab): AccountSearch {
  return tab === DEFAULT_ACCOUNT_TAB ? {} : { tab };
}

/** The word on a tab. Web copy: mobile has no such screen. */
export function accountTabLabel(tab: AccountTab, t: (key: string) => string): string {
  return t(`web:account.tabs.${tab}`);
}
